"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Check, Minus, Plus, ShoppingBag } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { fmtMxn } from "@/lib/currency";
import { useCart } from "@/components/CartProvider";
import type { PublicProduct } from "@/lib/products";

/**
 * Caja de compra del detalle de producto genérico. Para productos en modo
 * EGG (los del catálogo hoy) la compra se RUTEA por el motor de huevo: se
 * agrega una línea de huevo uniforme (mismo `id` que el flujo de huevo:
 * `${eggTypeId}:${presentationId}`) para conservar FIFO/APTO/comisiones/
 * trazabilidad y reusar el carrito + checkout existentes sin cambios.
 *
 * Productos MANUAL (no-huevo) se agregan como LÍNEA DE PRODUCTO (`productId`),
 * que vive solo en localStorage (el carrito del server es de huevo) y el
 * checkout manda como `{ productId, quantity }`. El precio mostrado es el de
 * menudeo; el mayoreo/volumen lo aplica el backend al cobrar si el cliente es
 * mayorista.
 */
export default function ProductBuyBox({ product }: { product: PublicProduct }) {
  const t = useTranslations("product");
  const { add } = useCart();
  // MOQ / case-pack (Fase 4): mínimo de compra + incremento. Default 1/1.
  const min = Math.max(1, Math.floor(Number(product.minOrderQty ?? 1)) || 1);
  const step = Math.max(1, Math.floor(Number(product.orderStep ?? 1)) || 1);
  const [qty, setQty] = useState(min);
  const [added, setAdded] = useState(false);

  const outOfStock = product.stock <= 0;
  const isEgg =
    product.pricingMode === "EGG" &&
    !!product.eggTypeId &&
    !!product.presentationId &&
    !!product.eggCapacity &&
    (product.eggSaleType === "MENUDEO" || product.eggSaleType === "MAYOREO");

  // Mayor cantidad válida (min + k·step) que no exceda el stock.
  const maxQty = Math.max(
    min,
    min + Math.floor(Math.max(0, product.stock - min) / step) * step,
  );
  // Ajusta a una cantidad válida: ≥min, múltiplo de step desde min, ≤maxQty.
  const clamp = (n: number) => {
    const bounded = Math.min(maxQty, Math.max(min, n));
    const snapped = min + Math.round((bounded - min) / step) * step;
    return Math.min(maxQty, Math.max(min, snapped));
  };

  const handleAdd = () => {
    if (isEgg) {
      // Producto de HUEVO → se rutea por el motor de huevo (línea de huevo
      // uniforme) para conservar FIFO/APTO/comisiones/trazabilidad.
      add(
        {
          id: `${product.eggTypeId}:${product.presentationId}`,
          eggTypeId: product.eggTypeId!,
          presentationId: product.presentationId!,
          name: product.name,
          presentationLabel: `${product.eggCapacity} piezas`,
          pricePerUnit: Number(product.price),
          imageUrl: product.images[0] ?? null,
          saleType: product.eggSaleType as "MENUDEO" | "MAYOREO",
          capacity: Number(product.eggCapacity),
        },
        qty,
      );
    } else {
      // Producto genérico (MANUAL) → línea de producto (sin huevo). El precio
      // mostrado es el de menudeo; el mayoreo/volumen lo aplica el backend al
      // cobrar si el cliente es mayorista.
      add(
        {
          id: `product:${product.id}`,
          productId: product.id,
          eggTypeId: "",
          presentationId: "",
          name: product.name,
          presentationLabel: product.category ?? "Producto",
          pricePerUnit: Number(product.price),
          imageUrl: product.images[0] ?? null,
        },
        qty,
      );
    }
    setAdded(true);
  };

  if (outOfStock) {
    return (
      <div className="mt-8">
        <button
          type="button"
          disabled
          className="w-full inline-flex items-center justify-center gap-2 bg-muted/30 text-dark/50 font-bold px-5 py-3 rounded-xl cursor-not-allowed"
        >
          {t("outOfStock")}
        </button>
      </div>
    );
  }

  const subtotal = Number(product.price) * qty;

  return (
    <div className="mt-8 space-y-4">
      {/* Cantidad + subtotal */}
      <div className="flex items-center justify-between gap-4">
        <div className="inline-flex items-center gap-1 bg-shell rounded-xl border border-muted/20">
          <button
            type="button"
            onClick={() => setQty((q) => clamp(q - step))}
            disabled={qty <= min}
            aria-label={t("decrease")}
            className="p-2.5 text-dark/60 hover:text-primary disabled:opacity-30"
          >
            <Minus size={16} />
          </button>
          <span className="w-8 text-center font-bold text-dark">{qty}</span>
          <button
            type="button"
            onClick={() => setQty((q) => clamp(q + step))}
            disabled={qty >= maxQty}
            aria-label={t("increase")}
            className="p-2.5 text-dark/60 hover:text-primary disabled:opacity-30"
          >
            <Plus size={16} />
          </button>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-dark/50 font-semibold">
            {t("subtotal")}
          </p>
          <p className="text-xl font-black text-dark">{fmtMxn(subtotal)}</p>
        </div>
      </div>

      {/* Nota de compra mínima / incremento (MOQ, Fase 4) */}
      {(min > 1 || step > 1) && (
        <p className="text-[11px] text-dark/55">
          {min > 1 && step > 1
            ? `Compra mínima ${min}, de ${step} en ${step}.`
            : min > 1
              ? `Compra mínima ${min}.`
              : `Se vende de ${step} en ${step}.`}
        </p>
      )}

      {/* Botón agregar */}
      <button
        type="button"
        onClick={handleAdd}
        className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-5 py-3 rounded-xl transition-colors"
      >
        {added ? <Check size={18} /> : <ShoppingBag size={18} />}
        {added ? t("addedToCart") : t("addToCart")}
      </button>

      {added && (
        <Link
          href="/cart"
          className="w-full inline-flex items-center justify-center gap-2 border border-primary/30 text-primary hover:bg-primary/5 font-semibold px-5 py-2.5 rounded-xl transition-colors"
        >
          {t("goToCart")}
        </Link>
      )}
    </div>
  );
}
